import { isRoleId, type RoleId } from '@onuw/roles-data'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { rolePath } from './slugs'

/**
 * A link to a role's page, under the role's name unless it has children. The prop is `id`: a `role` written as a
 * plain string reads as an ARIA role to the linter.
 */
export function RoleLink({ id, children }: { id: RoleId; children?: ReactNode }) {
  const { t } = useTranslation()
  return <Link to={rolePath(id)}>{children ?? t(`roles:${id}.title`)}</Link>
}

// [[seer]] or [[mason|Masons]]: a role's page; [label](/path/): another page of the site
const LINKS = /\[\[(\w+)(?:\|([^\]]+))?\]\]|\[([^\]]+)\]\((\/[^)]*)\)/g

/** A guide's plain text with its links: `[[seer]]`, `[[mason|Masons]]`, `[the rules](/rules/)`. */
export function RichText({ text }: { text: string }) {
  const parts: ReactNode[] = []
  let at = 0
  for (const m of text.matchAll(LINKS)) {
    const [whole, role, roleLabel, label, path] = m
    parts.push(text.slice(at, m.index))
    if (role && isRoleId(role))
      parts.push(
        <RoleLink key={m.index} id={role}>
          {roleLabel}
        </RoleLink>,
      )
    else if (label && path)
      parts.push(
        <Link key={m.index} to={path}>
          {label}
        </Link>,
      )
    else parts.push(whole)
    at = m.index + whole.length
  }
  parts.push(text.slice(at))
  return <>{parts}</>
}
